// Client identity model + generator. Each contract belongs to a persistent
// client with a sector, typology, loyalty, and 2 decision drivers. This is the
// data backbone used by the "Veille concurrents" feature (Marché tab).

export const CLIENT_SECTORS = [
  { id: 'agro',     name: 'Agroalimentaire',     icon: 'Wheat',          color: '#10B981' },
  { id: 'btp',      name: 'BTP / Matériaux',     icon: 'HardHat',        color: '#F59E0B' },
  { id: 'industrie',name: 'Industrie',           icon: 'Factory',        color: '#6B7280' },
  { id: 'retail',   name: 'Distribution / Retail', icon: 'ShoppingBag',  color: '#3B82F6' },
  { id: 'sante',    name: 'Santé / Pharma',      icon: 'Cross',          color: '#EF4444' },
  { id: 'chimie',   name: 'Chimie / ADR',        icon: 'FlaskConical',   color: '#8B5CF6' },
  { id: 'export',   name: 'Import-Export',       icon: 'Globe',          color: '#06B6D4' },
  { id: 'local',    name: 'Particuliers / PME',  icon: 'Users',          color: '#EC4899' },
];

export const CLIENT_TYPOLOGIES = [
  { id: 'grand_groupe',   name: 'Grand groupe',        loyaltyBase: 75, valueMultiplier: 1.40, switchDifficulty: 0.80 },
  { id: 'pme',            name: 'PME indépendante',    loyaltyBase: 55, valueMultiplier: 1.00, switchDifficulty: 0.45 },
  { id: 'cooperative',    name: 'Coopérative',         loyaltyBase: 85, valueMultiplier: 1.10, switchDifficulty: 0.65 },
  { id: 'startup',        name: 'Startup / E-commerce',loyaltyBase: 30, valueMultiplier: 0.85, switchDifficulty: 0.25 },
  { id: 'public',         name: 'Administration publique', loyaltyBase: 95, valueMultiplier: 1.30, switchDifficulty: 0.95 },
];

// Sector → preferred contract category (used by contract generator).
// Some sectors prefer 1 category, others have 2 options.
export const SECTOR_CATEGORIES = {
  agro:     ['Frigo/Pharma', 'Vrac/Palettes'],
  btp:      ['BTP / Matériaux', 'Lourd / Convoi exceptionnel'],
  industrie:['Vrac/Palettes', 'Lourd / Convoi exceptionnel'],
  retail:   ['Messagerie / Colis', 'Frigo/Pharma'],
  sante:    ['Frigo/Pharma', 'Spécialisé'],
  chimie:   ['Spécialisé', 'Dangerous'],
  export:   ['Export UE', 'Longue distance'],
  local:    ['Messagerie / Colis'],
};

// Sector → primary/secondary decision drivers (the arguments that matter most
// when challenging a contract).
export const SECTOR_DRIVERS = {
  agro:      { primary: 'délais',    secondary: 'prix' },
  btp:       { primary: 'prix',      secondary: 'délais' },
  industrie: { primary: 'qualité',   secondary: 'prix' },
  retail:    { primary: 'délais',    secondary: 'prix' },
  sante:     { primary: 'qualité',   secondary: 'délais' },
  chimie:    { primary: 'qualité',   secondary: 'prix' },
  export:    { primary: 'délais',    secondary: 'qualité' },
  local:     { primary: 'proximité', secondary: 'qualité' },
};

// 3 candidate names per sector → 24 clients in total at generation time.
const CLIENT_NAME_POOL = {
  agro: [
    'Coopérative Laitière de Vendôme',
    'Fromagerie des Hauts-de-Brie',
    'Domaine Maréchal & Fils',
  ],
  btp: [
    'Béton & Matériaux Vauclair',
    'Granulats du Nord Bressan',
    'Chantiers Lévêque-Roussel',
  ],
  industrie: [
    'Précismeca Ouest',
    'Fonderie Bertaud Industries',
    'Outillage Spécialisé Vienne',
  ],
  retail: [
    "Distrib'Centre Hyper",
    "Plat'forme E-Cargo",
    "Magasins Quotidien Express",
  ],
  sante: [
    'Pharmadis Vallée du Rhône',
    'Laboratoires BioRhin',
    'Vaccins & Sérums Bordeaux',
  ],
  chimie: [
    'ChimSud Industries',
    'Solvants Maréchal',
    'PétroDistri Centre',
  ],
  export: [
    'Eurolog International',
    'Trans-Bénélux Vermeer',
    'Iberica Cargo Sud',
  ],
  local: [
    'Mairie de Saint-Pierre-du-Lac',
    'Atelier Vitrier Lemoine',
    'Boulangeries Aubert Cie',
  ],
};

// Stable PRNG-ish deterministic id from name + sector.
const slugify = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Build the master client roster the game uses at launch. Each entry has a
 * loyalty score derived from the typology (with a small +/- delta to avoid
 * uniformity) and inherits sector drivers.
 */
export const generateClientRoster = () => {
  const roster = [];
  CLIENT_SECTORS.forEach(sector => {
    const names = CLIENT_NAME_POOL[sector.id] || [];
    names.forEach((name, idx) => {
      // Distribute typologies round-robin per sector so we cover all of them.
      // Specific overrides where a typology is more believable for a given name.
      let typology;
      if (sector.id === 'local' && idx === 0) typology = CLIENT_TYPOLOGIES.find(t => t.id === 'public');
      else if (sector.id === 'retail' && idx === 1) typology = CLIENT_TYPOLOGIES.find(t => t.id === 'startup');
      else if (sector.id === 'agro' && idx === 0) typology = CLIENT_TYPOLOGIES.find(t => t.id === 'cooperative');
      else if (sector.id === 'industrie' || sector.id === 'export') typology = CLIENT_TYPOLOGIES[idx % CLIENT_TYPOLOGIES.length];
      else typology = CLIENT_TYPOLOGIES.find(t => t.id === 'pme');

      const loyaltyDelta = (idx * 7 + sector.id.length * 3) % 11 - 5;
      const drivers = SECTOR_DRIVERS[sector.id];
      roster.push({
        id: `client-${slugify(name)}`,
        name,
        sectorId: sector.id,
        typologyId: typology.id,
        loyalty: Math.max(10, Math.min(99, typology.loyaltyBase + loyaltyDelta)),
        primaryDriver: drivers.primary,
        secondaryDriver: drivers.secondary,
      });
    });
  });
  return roster;
};

export const ARGUMENTS = [
  { id: 'prix',      label: 'Prix',      icon: 'Euro',     desc: 'Remise -15 % sur le tarif courant' },
  { id: 'délais',    label: 'Délais',    icon: 'Timer',    desc: 'Engagement de livraison express' },
  { id: 'qualité',   label: 'Qualité',   icon: 'Award',    desc: 'Réparation gratuite en cas d\'avarie' },
  { id: 'proximité', label: 'Proximité', icon: 'MapPin',   desc: 'Visite commerciale mensuelle' },
];

export const getClientById = (roster, id) => (roster || []).find(c => c.id === id);
export const getSector = (id) => CLIENT_SECTORS.find(s => s.id === id);
export const getTypology = (id) => CLIENT_TYPOLOGIES.find(t => t.id === id);
